import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FieldProps, getUiOptions } from '@rjsf/utils';
import { useTranslation } from 'react-i18next';
import { buttonClasses } from 'components/common/tailwindClassnames';
import { idToDataPath, resolveDataRef } from './referencePath';
import {
  batchField,
  exampleFor,
  outputTypeField,
  readAnnotation,
} from 'components/routes/outbound-transform/annotation';
import {
  previewInput,
  previewOutbound,
} from 'components/routes/outbound-transform/preview';
import {
  createEditorSessionStore,
  editorSessionKey,
  EditorSessionStore,
  liveEditorSession,
} from 'components/routes/outbound-transform/editorSessions';
import {
  OutboundTransformEditorModal,
  SUMMARY_TONE_CLASSES,
  usePreviewSummary,
} from 'components/routes/outbound-transform/OutboundTransformEditor';

type JqFormContext = {
  rootFormData?: any;
  idPrefix?: string;
  integrationId?: string;
  authToken?: string;
  editorSessions?: EditorSessionStore;
};

/**
 * An outbound endpoint's jq_filter, for fields whose ui_schema carries `gundi:jq_transform`
 * (attached by applyJqTransformFields). Renders the usual jq code editor plus a one-line
 * preview of what the endpoint would send, and opens the transformation editor. The data
 * type and batch flag are the sibling fields the annotation names; only the jq_filter
 * string is ever written.
 */
const JqTransformField = (props: FieldProps) => {
  const { t } = useTranslation();
  const summarize = usePreviewSummary();
  const {
    formData,
    onChange,
    idSchema,
    registry,
    uiSchema,
    schema,
    disabled,
    readonly,
    required,
    rawErrors,
    onBlur,
    onFocus,
    name,
    autofocus,
  } = props;
  const annotation = readAnnotation(uiSchema)!;
  const formContext = (registry.formContext ?? {}) as JqFormContext;
  const { rootFormData, idPrefix, integrationId, authToken } = formContext;
  // Outside SchemaForm there is no shared store; a private one just means no reopen after a remount.
  const ownSessions = useRef<EditorSessionStore | null>(null);
  const sessions =
    formContext.editorSessions ??
    (ownSessions.current ??= createEditorSessionStore());

  const fieldPath = useMemo(
    () => idToDataPath(idSchema.$id, registry.rootSchema, idPrefix),
    [idSchema.$id, registry.rootSchema, idPrefix],
  );
  const rawOutputType = fieldPath
    ? resolveDataRef(outputTypeField(annotation), fieldPath, rootFormData)
    : undefined;
  const outputType =
    typeof rawOutputType === 'string' && rawOutputType ? rawOutputType : null;
  const batch = !!(
    fieldPath && resolveDataRef(batchField(annotation), fieldPath, rootFormData)
  );

  const value: string = typeof formData === 'string' ? formData : '';
  const sessionKey =
    fieldPath && outputType
      ? editorSessionKey(integrationId, idPrefix, fieldPath, outputType)
      : null;
  // Reopens after a SchemaForm remount: the session outlives this component.
  const [editorOpen, setEditorOpen] = useState(
    () => !!(sessionKey && liveEditorSession(sessions, sessionKey, value)),
  );
  // The filter changed under an open editor (not by its Save): its draft is stale.
  useEffect(() => {
    if (
      editorOpen &&
      sessionKey &&
      sessions.has(sessionKey) &&
      !liveEditorSession(sessions, sessionKey, value)
    )
      setEditorOpen(false);
  }, [editorOpen, sessionKey, sessions, value]);
  const example = outputType ? exampleFor(annotation, outputType) : undefined;
  const preview = useMemo(
    () => previewOutbound(value, previewInput([example ?? {}], batch), batch),
    [value, example, batch],
  );
  const summary = summarize(preview, !!outputType);
  const readOnly = !!(disabled || readonly);

  const TextareaWidget = registry.widgets.TextareaWidget;
  const uiOptions = getUiOptions(uiSchema, registry.globalUiOptions);

  return (
    <div
      className="flex w-full flex-col gap-1.5"
      data-testid="JqTransformField"
    >
      <TextareaWidget
        id={idSchema.$id}
        name={name}
        schema={schema}
        uiSchema={uiSchema}
        value={formData}
        options={uiOptions}
        required={required}
        disabled={disabled}
        readonly={readonly}
        autofocus={autofocus}
        label={schema.title ?? name}
        placeholder={uiOptions.placeholder ?? ''}
        onChange={(next: any) => onChange(next)}
        onBlur={onBlur}
        onFocus={onFocus}
        rawErrors={rawErrors}
        registry={registry}
      />
      <div className="flex items-center justify-between gap-3">
        <span
          data-testid="JqTransformField-preview"
          className={`min-w-0 truncate text-xs ${SUMMARY_TONE_CLASSES[summary.tone]}`}
          title={summary.text}
        >
          {summary.text}
        </span>
        <button
          type="button"
          className={`${buttonClasses.lightGreenSm} shrink-0`}
          onClick={() => setEditorOpen(true)}
          disabled={!outputType}
          title={
            outputType
              ? undefined
              : t('Choose a Data Type to preview this filter')
          }
        >
          {t('Open transformation editor')}
        </button>
      </div>
      {editorOpen && outputType && sessionKey && (
        <OutboundTransformEditorModal
          sessions={sessions}
          sessionKey={sessionKey}
          value={value}
          // An empty filter is saved as unset, so the schema default (".") applies again.
          onSave={(jq) => onChange(jq || undefined)}
          onClose={() => setEditorOpen(false)}
          readOnly={readOnly}
          annotation={annotation}
          outputType={outputType}
          batch={batch}
          integrationId={integrationId}
          authToken={authToken}
        />
      )}
    </div>
  );
};

export default JqTransformField;
